import type { PuzzleModel } from '@arrow/game-engine';
import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';

import { type ArrowShape, arrowAnchor, cellCentre, flowStitch, U } from '../game/view.ts';

/**
 * The printed layer over the grid, in one SVG: the ink rules between cells, the solid
 * wedge arrows growing out of each clue block into its answer, and the Flow stitch.
 * One cell is `U` units square; rules keep a constant pixel width (non-scaling stroke).
 */

/** Wedges in cell units, drawn from the clue/answer boundary at (0, 0). */
export const ARROW_SHAPES: Record<ArrowShape, string> = {
	right: 'M0 -14L17 0L0 14Z',
	down: 'M-14 0L0 17L14 0Z',
	// In from the clue, turning the corner to run down (or right) along the answer.
	'right-down': 'M0 -4H20V6H30L16 23L2 6H12V4H0Z',
	'down-right': 'M-4 0V20H6V30L23 16L6 2V12H4V0Z',
};

/** Where each wedge scales from: its root on the boundary. */
export const ARROW_ORIGIN: Record<ArrowShape, string> = {
	right: '0% 50%',
	down: '50% 0%',
	'right-down': '0% 0%',
	'down-right': '0% 0%',
};

interface OverlayProps {
	model: PuzzleModel;
	activeAnswerId: string | null;
	links: { chain: string[]; serial: number } | null;
	nudge: { serial: number; delays: Record<string, number> } | null;
	/** Each answer's place in the solve: the completion fires the arrows in this order. */
	order: Map<string, number>;
	/** The best Flow chain: its arrows stay cobalt on the finished map. */
	best: ReadonlySet<string>;
	/**
	 * Zoomed out on a big board: the solved answers (their wedges go hollow), and the
	 * wedges print bolder, as the arrows-only overview. Null at reading size.
	 */
	spent?: ReadonlySet<string> | null;
}

/** Wedge scale on the arrows-only overview, where a cell is about half reading size. */
const FAR_WEDGE = 1.45;

export const BoardOverlay = memo(function BoardOverlay({ model, activeAnswerId, links, nudge, order, best, spent = null }: OverlayProps) {
	const clues = useMemo(() => Object.values(model.clues), [model]);
	const rules = useMemo(() => {
		const W = model.width * U;
		const H = model.height * U;
		let d = '';
		for (let k = 1; k < model.width; k++) d += `M${k * U} 0V${H}`;
		for (let k = 1; k < model.height; k++) d += `M0 ${k * U}H${W}`;
		return d;
	}, [model]);

	return (
		<svg
			className="overlay"
			viewBox={`0 0 ${model.width * U} ${model.height * U}`}
			preserveAspectRatio="none"
			aria-hidden="true"
		>
			<path className="rules" d={rules} />
			<g className="arrows">
				{clues.map((clue) => {
					const { x, y, shape } = arrowAnchor(model, clue);
					const answer = model.answers[clue.answerId];
					const delay = nudge?.delays[clue.answerId];
					const cls = [
						'arw',
						clue.answerId === activeAnswerId && 'on',
						best.has(clue.answerId) && 'best',
						spent?.has(clue.answerId) && 'spent',
					]
						.filter(Boolean)
						.join(' ');
					return (
						<g key={clue.id} transform={spent ? `translate(${x} ${y}) scale(${FAR_WEDGE})` : `translate(${x} ${y})`}>
							<g
								// Keyed on the nudge so it replays for each solve that feeds this answer.
								key={delay !== undefined ? `n${nudge!.serial}` : 'n'}
								className={delay !== undefined ? `nud nudge ${answer?.axis === 'down' ? 'nud-d' : 'nud-a'}` : 'nud'}
								style={delay !== undefined ? ({ '--i': delay } as CSSProperties) : undefined}
							>
								<path
									className={cls}
									d={ARROW_SHAPES[shape]}
									style={{ transformOrigin: ARROW_ORIGIN[shape], '--q': order.get(clue.answerId) ?? 0 } as CSSProperties}
								/>
							</g>
						</g>
					);
				})}
			</g>
			{links && links.chain.length > 1 && (
				<FlowStitch key={links.serial} model={model} prev={links.chain[links.chain.length - 2]!} next={links.chain[links.chain.length - 1]!} />
			)}
		</svg>
	);
});

/**
 * How far the stitch runs off the cell centres, toward the lower right (in cell units).
 * Through the centres it would cross the letters out; offset, it runs under an across
 * word and beside a down word, like a seam.
 */
const STITCH_OFFSET = U * 0.3;

/** Flow: a cobalt stitch drawn along the two answers, turning at the cell they share. */
function FlowStitch({ model, prev, next }: { model: PuzzleModel; prev: string; next: string }) {
	const stitch = flowStitch(model, prev, next);
	if (!stitch) return null;
	const d =
		'M' +
		stitch.cells
			.map((c) => {
				const [x, y] = cellCentre(model, c);
				return `${x + STITCH_OFFSET} ${y + STITCH_OFFSET}`;
			})
			.join('L');
	return <path className="stitch" d={d} pathLength={1} />;
}
